import React, { useState, useRef, useEffect } from 'react'
import { Pencil, Check, X, Loader2 } from 'lucide-react'

const API = process.env.REACT_APP_BACKEND_URL

export default function InlineDateCell({ sinistre, field, onUpdated, label }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const ref = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setEditing(false)
      }
    }
    if (editing) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [editing])

  function startEdit() {
    const raw = sinistre[field]
    if (raw) {
      const d = new Date(raw)
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      setValue(`${yyyy}-${mm}-${dd}`)
    } else {
      setValue('')
    }
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  async function handleSave() {
    if (!value) return
    setSaving(true)
    try {
      const payload = {}
      payload[field] = value
      const res = await fetch(`${API}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (res.ok && onUpdated) onUpdated()
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  const raw = sinistre[field]
  const displayDate = raw
    ? new Date(raw).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '--'

  if (editing) {
    return (
      <div ref={ref} className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="date"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false) }}
          className="text-[11px] border border-indigo-300 rounded px-1.5 py-0.5 w-[130px] focus:outline-none focus:ring-1 focus:ring-indigo-400"
          data-testid={`date-input-${field}`}
        />
        <button onClick={handleSave} disabled={saving || !value}
          className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-30"
          data-testid={`date-save-${field}`}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
        </button>
        <button onClick={() => setEditing(false)} className="p-0.5 text-red-400 hover:bg-red-50 rounded">
          <X size={12} />
        </button>
      </div>
    )
  }

  return (
    <span
      className="text-[10px] text-slate-500 cursor-pointer hover:text-indigo-600 hover:underline decoration-dotted inline-flex items-center gap-1 group"
      onClick={e => { e.stopPropagation(); startEdit() }}
      title={`Modifier ${label || field}`}
      data-testid={`date-display-${field}`}
    >
      {displayDate}
      <Pencil size={10} className="opacity-0 group-hover:opacity-100 transition-opacity text-indigo-400" />
    </span>
  )
}
